import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, HeartHandshake, Check, X, Clock, Trash2, ExternalLink } from 'lucide-react';

const STATUSES = [
  { key: 'pending', label: 'Pending Review', color: 'bg-[#FFF3E0] text-[#E65100]' },
  { key: 'approved', label: 'Approved', color: 'bg-[#E8F5E9] text-[#1B5E20]' },
  { key: 'active', label: 'Active', color: 'bg-[#E3F2FD] text-[#0D47A1]' },
  { key: 'rejected', label: 'Rejected', color: 'bg-[#FFEBEE] text-[#B71C1C]' },
  { key: 'archived', label: 'Archived', color: 'bg-gray-100 text-gray-600' },
];

const PROGRAMS = [
  { key: 'day_program', label: 'Explore', color: 'bg-[#F3E5F5] text-[#4A148C]' },
  { key: 'employment', label: 'Employment', color: 'bg-[#FFF3E0] text-[#E65100]' },
  { key: 'in_home', label: 'IHCS', color: 'bg-[#E8F5E9] text-[#1B5E20]' },
  { key: 'private_pay', label: 'Private Pay', color: 'bg-[#E1F5FE] text-[#01579B]' },
  { key: 'general', label: 'General', color: 'bg-gray-100 text-gray-700' },
];

export default function VolunteersPage() {
  const { api, user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [hoursOpen, setHoursOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.set('status', filter);
      if (programFilter) params.set('program', programFilter);
      const qs = params.toString();
      const { data } = await api.get(`/volunteers${qs ? `?${qs}` : ''}`);
      setList(data);
    } catch (e) {
      toast.error('Failed to load volunteers');
    } finally { setLoading(false); }
  }, [api, filter, programFilter]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (v, status) => {
    try {
      await api.put(`/volunteers/${v.id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
      if (selected?.id === v.id) setSelected({ ...selected, status });
    } catch (e) { toast.error('Failed to update'); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.name}? This can't be undone.`)) return;
    try {
      await api.delete(`/volunteers/${v.id}`);
      toast.success('Deleted');
      setSelected(null); load();
    } catch (e) { toast.error('Delete failed — administrators only'); }
  };

  const badge = (status) => {
    const s = STATUSES.find(x => x.key === status) || STATUSES[0];
    return <Badge className={`${s.color} border-0`}>{s.label}</Badge>;
  };
  const programBadge = (program) => {
    const p = PROGRAMS.find(x => x.key === program) || PROGRAMS[4];
    return <Badge className={`${p.color} border-0`}>{p.label}</Badge>;
  };

  const totalHours = (v) => (v.hours_logged || []).reduce((s, h) => s + (h.hours || 0), 0);

  const updateField = async (v, field, value) => {
    try {
      const { data } = await api.put(`/volunteers/${v.id}`, { [field]: value });
      toast.success('Updated');
      if (selected?.id === v.id) setSelected(data);
      load();
    } catch (e) { toast.error('Update failed'); }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="volunteers-page">
      <div className="flex items-center gap-3 mb-1">
        <HeartHandshake className="h-8 w-8 text-[#6B3FA0]" />
        <h1 className="text-4xl font-bold">Volunteers</h1>
      </div>
      <p className="text-gray-600 mb-6">Applicant pipeline. Review, approve, and log hours for active volunteers.</p>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-3">
        <button onClick={() => setFilter('')}
                className={`px-3 py-1.5 rounded-full text-sm ${!filter ? 'bg-[#6B3FA0] text-white' : 'bg-white border'}`}
                data-testid="filter-all">All statuses</button>
        {STATUSES.map(s => (
          <button key={s.key} onClick={() => setFilter(s.key)}
                  className={`px-3 py-1.5 rounded-full text-sm ${filter === s.key ? 'bg-[#6B3FA0] text-white' : 'bg-white border'}`}
                  data-testid={`filter-${s.key}`}>
            {s.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setProgramFilter('')}
                className={`px-3 py-1 rounded-full text-xs ${!programFilter ? 'bg-gray-800 text-white' : 'bg-white border'}`}
                data-testid="pfilter-all">All programs</button>
        {PROGRAMS.map(p => (
          <button key={p.key} onClick={() => setProgramFilter(p.key)}
                  className={`px-3 py-1 rounded-full text-xs ${programFilter === p.key ? 'bg-gray-800 text-white' : 'bg-white border'}`}
                  data-testid={`pfilter-${p.key}`}>
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#6B3FA0]" /></div>
      ) : list.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-500">
          No volunteer applications yet. Share <a className="underline" href="/volunteer" target="_blank" rel="noopener noreferrer">the volunteer form</a>.
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {list.map(v => (
            <Card key={v.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-lg">{v.name}</span>
                    {badge(v.status)}
                    {programBadge(v.program)}
                    {v.applicant_type === 'returning' && <Badge variant="outline" className="text-xs">Returning</Badge>}
                    {v.background_check_status === 'cleared' && <Badge className="bg-[#E8F5E9] text-[#1B5E20] border-0">BG cleared</Badge>}
                  </div>
                  <div className="text-sm text-gray-500 mt-0.5">
                    {v.email}{v.phone ? ` · ${v.phone}` : ''} · Applied {new Date(v.applied_at).toLocaleDateString()}
                  </div>
                  {v.availability && <div className="text-xs text-gray-500 mt-1 truncate">🕒 {v.availability}</div>}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500 mr-2">{totalHours(v).toFixed(1)}h logged</span>
                  <Button size="sm" variant="outline" onClick={() => setSelected(v)}
                          data-testid={`view-${v.id}`}>View</Button>
                  {v.status === 'pending' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => changeStatus(v, 'approved')}
                              data-testid={`approve-${v.id}`}
                              className="border-green-500 text-green-700 hover:bg-green-50">
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => changeStatus(v, 'rejected')}
                              data-testid={`reject-${v.id}`}
                              className="border-red-500 text-red-700 hover:bg-red-50">
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={o => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="volunteer-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selected.name} {badge(selected.status)}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <FieldRow k="Email" v={selected.email} />
                <FieldRow k="Phone" v={selected.phone} />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-0.5">Program</div>
                    <select value={selected.program || 'general'}
                            onChange={e => updateField(selected, 'program', e.target.value)}
                            className="border rounded px-2 py-1 text-sm w-full"
                            data-testid="edit-program">
                      {PROGRAMS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-0.5">Applicant type</div>
                    <select value={selected.applicant_type || 'new'}
                            onChange={e => updateField(selected, 'applicant_type', e.target.value)}
                            className="border rounded px-2 py-1 text-sm w-full"
                            data-testid="edit-applicant-type">
                      <option value="new">New volunteer</option>
                      <option value="returning">Returning</option>
                    </select>
                  </div>
                </div>
                <FieldRow k="Availability" v={selected.availability} />
                <FieldRow k="Skills" v={selected.skills} />
                <FieldRow k="Why volunteer" v={selected.why_volunteer} />
                <FieldRow k="References" v={selected.references} />
                <FieldRow k="Background check" v={
                  `${selected.background_check_consent ? 'Consented' : 'Not consented'} — status: ${selected.background_check_status || 'pending'}`
                } />

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold flex items-center gap-1.5"><Clock className="h-4 w-4" /> Hours logged</div>
                    <Button size="sm" variant="outline" onClick={() => setHoursOpen(true)}
                            disabled={!['approved', 'active'].includes(selected.status)}
                            data-testid="log-hours-btn">Log hours</Button>
                  </div>
                  {(selected.hours_logged || []).length === 0 ? (
                    <p className="text-sm text-gray-500">No hours logged yet.</p>
                  ) : (
                    <ul className="text-sm space-y-1">
                      {selected.hours_logged.map(h => (
                        <li key={h.id} className="flex justify-between">
                          <span>{h.date} — {h.activity || 'volunteer work'}</span>
                          <span className="font-medium">{h.hours}h</span>
                        </li>
                      ))}
                      <li className="flex justify-between border-t pt-1 mt-1 font-semibold">
                        <span>Total</span><span>{totalHours(selected).toFixed(1)}h</span>
                      </li>
                    </ul>
                  )}
                </div>
              </div>

              <DialogFooter className="flex-wrap gap-2">
                <select value={selected.status}
                        onChange={e => changeStatus(selected, e.target.value)}
                        className="border rounded px-3 py-1.5 text-sm"
                        data-testid="status-select">
                  {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                {user?.role === 'administrator' && (
                  <Button variant="outline" onClick={() => remove(selected)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          data-testid="delete-volunteer-btn">
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                )}
                <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Log Hours Dialog */}
      <HoursDialog open={hoursOpen} onClose={() => setHoursOpen(false)} volunteer={selected}
                   api={api} onLogged={(fresh) => { setSelected(fresh); load(); }} />
    </div>
  );
}

function FieldRow({ k, v }) {
  if (!v) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500 mb-0.5">{k}</div>
      <div className="text-sm whitespace-pre-wrap">{v}</div>
    </div>
  );
}

function HoursDialog({ open, onClose, volunteer, api, onLogged }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [hours, setHours] = useState('');
  const [activity, setActivity] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!hours || parseFloat(hours) <= 0) { toast.error('Enter valid hours'); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/volunteers/${volunteer.id}/hours`, {
        date, hours: parseFloat(hours), activity,
      });
      toast.success('Hours logged');
      onLogged(data);
      onClose();
      setHours(''); setActivity('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="hours-dialog">
        <DialogHeader><DialogTitle>Log hours for {volunteer?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Date</Label><Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="hours-date" /></div>
          <div><Label>Hours</Label><Input type="number" step="0.25" min="0" max="24" value={hours} onChange={e => setHours(e.target.value)} data-testid="hours-input" /></div>
          <div><Label>Activity (optional)</Label><Textarea value={activity} onChange={e => setActivity(e.target.value)} rows={2} data-testid="hours-activity" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-hours-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
